import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import type { ParserErrorDto } from '@poker-stats/contracts';
import { deleteParserError, loadParserErrors } from '../../containers/ParserErrorsPage/reducer';
import {
  selectParserErrorDeletingId,
  selectParserErrors,
  selectParserErrorsError,
  selectParserErrorsLoading,
  selectParserErrorsPage,
  selectParserErrorsPageSize,
  selectParserErrorsTotal,
} from '../../containers/ParserErrorsPage/selectors';
import V2AdminPageHeading from '../components/V2AdminPageHeading';
import { AdminConfirmButton, AdminDialog, AdminNotice, AdminTable } from '../components/V2AdminUI';

const V2AdminParserErrors: React.FC = () => {
  const { t } = useTranslation('translation');
  const dispatch = useDispatch();
  const items = useSelector(selectParserErrors);
  const total = useSelector(selectParserErrorsTotal);
  const page = useSelector(selectParserErrorsPage);
  const pageSize = useSelector(selectParserErrorsPageSize);
  const loading = useSelector(selectParserErrorsLoading);
  const deletingId = useSelector(selectParserErrorDeletingId);
  const error = useSelector(selectParserErrorsError);
  const [selectedError, setSelectedError] = React.useState<ParserErrorDto>();

  React.useEffect(() => {
    dispatch(loadParserErrors(undefined));
  }, [dispatch]);

  return (
    <>
      <V2AdminPageHeading
        title={t('admin_parser_errors')}
        description={t('admin_parser_errors_description')}
      />
      <div className="v2-admin-parser-errors">
        {error ? (
          <AdminNotice
            type="error"
            action={
              <button
                type="button"
                className="outline-button"
                onClick={() => dispatch(loadParserErrors(undefined))}
              >
                {t('retry')}
              </button>
            }
          >
            {error}
          </AdminNotice>
        ) : null}
        <section className="settings-panel v2-admin-table-panel">
          <div className="settings-panel__head v2-admin-table-panel__head">
            <h2>{t('admin_parser_errors_log')}</h2>
            <p className="v2-admin-table-panel__meta">{t('admin_ui_total', { count: total })}</p>
          </div>
          <AdminTable<ParserErrorDto>
            rows={items}
            rowKey="id"
            loading={loading}
            caption={t('admin_parser_errors_log')}
            minWidth={760}
            emptyText={t('admin_parser_errors_empty')}
            pagination={{
              page,
              pageSize,
              total,
              onChange: (nextPage) => dispatch(loadParserErrors({ page: nextPage, pageSize })),
              onPageSizeChange: (size) => dispatch(loadParserErrors({ page: 1, pageSize: size })),
            }}
            columns={[
              {
                key: 'date',
                title: t('admin_parser_error_date'),
                render: (row) => dayjs(row.parsedAt).format('DD.MM.YY HH:mm:ss'),
              },
              {
                key: 'room',
                title: t('admin_parser_error_room'),
                render: (row) => <span className="v2-admin-room-tag">{row.room}</span>,
              },
              {
                key: 'hand',
                title: t('admin_parser_error_hand'),
                render: (row) => (
                  <>
                    <pre className="v2-admin-hand-preview">{row.handText}</pre>
                    <button
                      type="button"
                      className="v2-admin-text-link"
                      onClick={() => setSelectedError(row)}
                    >
                      {t('admin_parser_error_view')}
                    </button>
                  </>
                ),
              },
              {
                key: 'actions',
                title: t('admin_parser_error_actions'),
                render: (row) => (
                  <AdminConfirmButton
                    title={t('admin_parser_error_delete_confirm')}
                    disabled={deletingId !== undefined}
                    onConfirm={() => dispatch(deleteParserError(row.id))}
                  >
                    {deletingId === row.id ? '…' : t('delete')}
                  </AdminConfirmButton>
                ),
              },
            ]}
          />
        </section>
        <AdminDialog
          open={Boolean(selectedError)}
          title={t('admin_parser_error_hand_title', { id: selectedError?.id ?? '' })}
          onClose={() => setSelectedError(undefined)}
        >
          <pre className="v2-admin-hand-text">{selectedError?.handText}</pre>
        </AdminDialog>
      </div>
    </>
  );
};

export default V2AdminParserErrors;
